import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';

import WebHttpClient from '~src/httpClient/WebHttpClient';

import {
  FETCH_SITE_ADMIN_RESOURCES_REQUEST,
  fetchSiteAdminResourcesFailure,
  fetchSiteAdminResourcesSuccess
} from '../actions/SiteAdminResourceActions';
import {
  GetActorVisibleResourceTypesRequest,
  IResourceTypesForActorResource,
  IResourceTypesForActorResult
} from '../serverApi/requests/GetActorVisibleResourceTypesRequest';

/**
 * Saga to fetch site admin resources from the backend.
 * 
 * Calls GET /api/actorVisibleResourceTypes to get which admin resource types
 * (User, Group, Task, Job) the current actor can access.
 * 
 * Only triggered when isAdminProfilesEnabledOnServer is ON
 */
export function* handleFetchSiteAdminResources(): SagaIterator {
  try {
    const result: IResourceTypesForActorResult = yield call(
      [WebHttpClient, WebHttpClient.sendRequest],
      new GetActorVisibleResourceTypesRequest()
    );

    // Normalize resources: API can return array directly or wrapped as { resource: [...] }
    const resources = result?.resources;
    const resourceItems: IResourceTypesForActorResource[] = Array.isArray(resources)
      ? resources
      : (resources?.resource ?? []);

    const allowedResources = new Set<string>(
      resourceItems
        .filter(r => r.isAllowed === true || r.isAllowed === 'true')
        .map(r => r.type)
        .filter((t): t is string => Boolean(t))
    );

    yield put(fetchSiteAdminResourcesSuccess(allowedResources));
  } catch (error) {
    // On error, dispatch failure action
    yield put(fetchSiteAdminResourcesFailure());
  }
}

/**
 * Root saga for site admin resources.
 */
export function* rootSiteAdminResourcesSaga(): SagaIterator {
  yield takeLatest(FETCH_SITE_ADMIN_RESOURCES_REQUEST, handleFetchSiteAdminResources);
}
